/**
 * Order rate engine — applies a customer's rate card to the order's CHARGES.
 *
 * Pure functions only (no React): resolve which fuel surcharge applies, compute
 * the fuel-surcharge base, recompute locked (computed) lines, and build charge
 * lines from the org catalogue + the customer's additional rates. The panel
 * wires these to live state; invoicing later reads the persisted NET.
 */
import type {
  ChargeCostDefinition,
  CustomerAdditionalCharge,
  CustomerFuelSurcharge,
} from '@my/tms-api';
import { newChargeCostLineId, type ChargeCostLine } from './charges';

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Region membership for Region-scoped fuel surcharges. The shared `COUNTRIES`
 * catalogue carries no region data, so we cover the common `EU` case here;
 * extend as more region codes are used. An unknown region simply doesn't match
 * and the resolver falls back to the flat `All` rate.
 */
const REGION_MEMBERS: Record<string, ReadonlySet<string>> = {
  EU: new Set([
    'IE', 'FR', 'DE', 'ES', 'IT', 'NL', 'BE', 'LU', 'PT', 'AT', 'DK', 'SE', 'FI',
    'PL', 'CZ', 'SK', 'HU', 'RO', 'BG', 'HR', 'SI', 'EE', 'LV', 'LT', 'GR', 'CY', 'MT',
  ]),
};

function inRegion(countryIso: string, regionCode: string): boolean {
  const members = REGION_MEMBERS[regionCode.toUpperCase()];
  return members ? members.has(countryIso.toUpperCase()) : false;
}

/** Active on `today` (all ISO `yyyy-mm-dd`, so string compare is date-correct). */
function isActive(fs: CustomerFuelSurcharge, today: string): boolean {
  if (fs.effectiveFrom > today) return false;
  if (fs.effectiveTo && fs.effectiveTo < today) return false;
  return true;
}

const pickHighest = (rows: CustomerFuelSurcharge[]) =>
  rows.reduce((best, s) => (s.ratePercent > best.ratePercent ? s : best));

/**
 * Pick the fuel surcharge that applies to a delivery in `deliveryCountryIso`
 * on `today`. Specificity Country → Region → flat `All`; within a tier, the
 * highest rate wins (there should be one active per territory, but a duplicate
 * shouldn't silently under-charge). Returns null when none is active/matching.
 */
export function resolveFuelSurcharge(
  surcharges: readonly CustomerFuelSurcharge[],
  deliveryCountryIso: string | null,
  today: string,
): CustomerFuelSurcharge | null {
  const active = surcharges.filter((s) => isActive(s, today));
  const iso = deliveryCountryIso?.toUpperCase() ?? null;

  const tiers: CustomerFuelSurcharge[][] = [
    iso
      ? active.filter((s) => s.scopeKind === 'Country' && (s.scopeCode ?? '').toUpperCase() === iso)
      : [],
    iso ? active.filter((s) => s.scopeKind === 'Region' && inRegion(iso, s.scopeCode ?? '')) : [],
    active.filter((s) => s.scopeKind === 'All'),
  ];

  for (const tier of tiers) if (tier.length > 0) return pickHighest(tier);
  return null;
}

/** Sum the NET of the FS-applicable charge lines — the fuel-surcharge base. */
export function computeBase(charges: readonly ChargeCostLine[]): number {
  return charges.filter((l) => l.fsApplicable).reduce((s, l) => s + l.net, 0);
}

/**
 * Recompute every locked rate line's `net` = `rate% × base`. Returns the same
 * array reference when nothing changed so callers can skip needless re-renders.
 */
export function recomputeLocked(charges: ChargeCostLine[]): ChargeCostLine[] {
  const base = computeBase(charges);
  let changed = false;
  const next = charges.map((l) => {
    if (l.isLocked && l.rate != null) {
      const net = round2((l.rate / 100) * base);
      if (net !== l.net) {
        changed = true;
        return { ...l, net };
      }
    }
    return l;
  });
  return changed ? next : charges;
}

/** Build the locked Fuel Surcharge line from a resolved surcharge. */
export function buildFuelSurchargeLine(
  fsDef: ChargeCostDefinition,
  surcharge: CustomerFuelSurcharge,
): ChargeCostLine {
  return {
    id: newChargeCostLineId(),
    definitionId: fsDef.id,
    name: fsDef.displayName,
    type: 'FuelSurcharge',
    net: 0, // set by recomputeLocked
    isProtected: fsDef.isProtected,
    rate: surcharge.ratePercent,
    isLocked: true,
    fsApplicable: false,
  };
}

/**
 * Build a charge line for a catalogue definition picked from + Add, applying
 * the customer's additional rate when present:
 *   • Flat          → prefill the amount (editable)
 *   • PercentOfBase → rate + locked (computed off the base, like fuel surcharge)
 *   • PerHour       → prefill the hourly rate as a hint (editable; operator
 *                     enters the final amount)
 * `fsApplicable` is copied from the additional (a %-of-base line never feeds
 * the base — no compounding).
 */
export function buildChargeLine(
  def: ChargeCostDefinition,
  additional: CustomerAdditionalCharge | undefined,
): ChargeCostLine {
  const line: ChargeCostLine = {
    id: newChargeCostLineId(),
    definitionId: def.id,
    name: def.displayName,
    type: def.type,
    net: 0,
    isProtected: def.isProtected,
    rate: null,
    isLocked: false,
    fsApplicable: additional?.fsApplicable ?? false,
  };
  if (!additional) return line;
  switch (additional.rateType) {
    case 'Flat':
      return { ...line, net: additional.rate };
    case 'PercentOfBase':
      return { ...line, rate: additional.rate, isLocked: true, fsApplicable: false };
    case 'PerHour':
      return { ...line, rate: additional.rate }; // hint only; operator enters amount
    default:
      return line;
  }
}

/**
 * Apply the customer's rate card to the current CHARGE lines: mark the
 * Transport Charge FS-applicable, ensure the Fuel Surcharge line matches the
 * resolved rate (add it, or sync its rate), then recompute locked lines. Costs
 * are untouched. Idempotent — safe to run whenever the rate card / definitions
 * arrive or the customer changes. Operator edits to other lines are preserved.
 */
export function applyCustomerRates(
  charges: ChargeCostLine[],
  opts: {
    definitions: readonly ChargeCostDefinition[];
    fuelSurcharges: readonly CustomerFuelSurcharge[];
    deliveryCountryIso: string | null;
    today: string;
    /** New-order preview: seed a Transport Charge line if none exists yet (the
     *  BE seeds it at creation, but a not-yet-saved order has no lines). */
    seedTransportIfMissing?: boolean;
  },
): ChargeCostLine[] {
  const { definitions, fuelSurcharges, deliveryCountryIso, today } = opts;

  let next = [...charges];

  // New-order preview only: ensure the Transport Charge default is visible.
  if (opts.seedTransportIfMissing && !next.some((l) => l.type === 'TransportCharge')) {
    const transportDef = definitions.find((d) => d.type === 'TransportCharge');
    if (transportDef) {
      next.unshift({
        id: newChargeCostLineId(),
        definitionId: transportDef.id,
        name: transportDef.displayName,
        type: 'TransportCharge',
        net: 0,
        isProtected: transportDef.isProtected,
        rate: null,
        isLocked: false,
        fsApplicable: true,
      });
    }
  }

  // Transport Charge always feeds the fuel-surcharge base.
  next = next.map((l) =>
    l.type === 'TransportCharge' && !l.fsApplicable ? { ...l, fsApplicable: true } : l,
  );

  const fs = resolveFuelSurcharge(fuelSurcharges, deliveryCountryIso, today);
  const fsDef = definitions.find((d) => d.type === 'FuelSurcharge');
  if (fs && fsDef) {
    const idx = next.findIndex((l) => l.type === 'FuelSurcharge');
    if (idx >= 0) {
      next = next.map((l, i) =>
        i === idx
          ? {
              ...l,
              rate: fs.ratePercent,
              isLocked: true,
              fsApplicable: false,
              definitionId: fsDef.id,
              name: fsDef.displayName,
            }
          : l,
      );
    } else {
      next = [...next, buildFuelSurchargeLine(fsDef, fs)];
    }
  }

  return recomputeLocked(next);
}
